import React, { memo } from "react";
import { formatLastMessagePreview, formatPhone, normalizePhone } from "../../utils/formatters";
import { SERVICE_STATUS_CONFIG } from "../../utils/constants";
import ChannelBadge from "../common/ChannelBadge";
import "./ConversationItem.css";

/**
 * Item de conversa na lista (multi-canal: WhatsApp + Instagram + Messenger).
 */
const ConversationItem = memo(({
  conversation,
  isSelected,
  unreadCount = 0,
  onClick,
}) => {
  const statusConfig = SERVICE_STATUS_CONFIG[conversation.service_status] || SERVICE_STATUS_CONFIG.new;
  const channel = conversation.channel || 'whatsapp';

  // Para Instagram, nunca formatar como telefone — usar nome ou @handle direto.
  const isInstagram = channel === 'instagram';
  const displayName = isInstagram
    ? (conversation.name || conversation.channel_user_id || 'Instagram')
    : (conversation.name?.startsWith("+")
        ? formatPhone(conversation.name)
        : conversation.name || formatPhone(conversation.phone));

  return (
    <div
      className={`sc-conversation-item ${isSelected ? "sc-conversation-item--active" : ""}`}
      onClick={onClick}
    >
      {/* Indicador de status */}
      <div
        className="sc-conversation-item__status-indicator"
        style={{ backgroundColor: statusConfig.color }}
        title={statusConfig.label}
      />

      {/* Avatar */}
      <div className="sc-conversation-item__avatar">
        <img
          src={conversation.avatar}
          alt={displayName}
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              displayName
            )}&background=random`;
          }}
        />
        {conversation.bot_status === 0 && (
          <span className="sc-conversation-item__bot-badge" title="Bot desativado">
            🤖
          </span>
        )}
        <ChannelBadge channel={channel} variant="avatar" />
      </div>

      {/* Detalhes */}
      <div className="sc-conversation-item__details">
        <div className="sc-conversation-item__header">
          <span
            className={`sc-conversation-item__name ${
              unreadCount > 0 ? "sc-conversation-item__name--unread" : ""
            }`}
          >
            {displayName}
          </span>
          <span className="sc-conversation-item__time">
            {conversation.lastMessageTime}
          </span>
        </div>

        <div className="sc-conversation-item__preview">
          <span className="sc-conversation-item__message">
            {conversation.fromMe && (
              <span className="sc-conversation-item__from-me">✓ </span>
            )}
            {formatLastMessagePreview(conversation.lastMessage)}
          </span>
        </div>

        {/* Meta info */}
        <div className="sc-conversation-item__meta">
          <div className="sc-conversation-item__meta-left">
            {conversation.current_department_name && (
              <span className="sc-conversation-item__department">
                🏢 {conversation.current_department_name}
              </span>
            )}
            {conversation.current_attendant_name && (
              <span className="sc-conversation-item__attendant">
                👤 {(() => { const parts = conversation.current_attendant_name.split(" "); return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0]; })()}
              </span>
            )}
          </div>
          {(conversation.priority || conversation.temperature || conversation.deal_value) && (
            <div className="sc-conversation-item__meta-right">
              {conversation.deal_value && (
                <span className="sc-conversation-item__tag sc-conversation-item__tag--value">
                  {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(conversation.deal_value)}
                </span>
              )}
              {conversation.priority && (
                <span className={`sc-conversation-item__tag sc-conversation-item__tag--${conversation.priority === "high" ? "danger" : conversation.priority === "medium" ? "warning" : "info"}`}>
                  {conversation.priority === "high" ? "Alta" : conversation.priority === "medium" ? "Média" : "Baixa"}
                </span>
              )}
              {conversation.temperature && (
                <span className={`sc-conversation-item__tag sc-conversation-item__tag--${conversation.temperature === "hot" ? "danger" : conversation.temperature === "warm" ? "warning" : "info"}`}>
                  {conversation.temperature === "hot" ? "Quente" : conversation.temperature === "warm" ? "Morno" : "Frio"}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Badge de não lidas */}
      {unreadCount > 0 && (
        <div className="sc-conversation-item__unread">
          {unreadCount > 9 ? "9+" : unreadCount}
        </div>
      )}
    </div>
  );
});

ConversationItem.displayName = "ConversationItem";

export default ConversationItem;
